import { useState, useEffect } from 'react';
import { getRouteOrder, saveRouteOrder, getCustomers } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowUp, ArrowDown, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export const RouteOrder = () => {
  const [routes, setRoutes] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orderRes, customersRes] = await Promise.all([
        getRouteOrder(),
        getCustomers(),
      ]);

      // 顧客データから全ルート番号を取得
      const routeSet = new Set();
      (customersRes.data || []).forEach(c => {
        if (c.route_number) routeSet.add(c.route_number);
      });
      const allRoutesList = [...routeSet].sort();
      setAllRoutes(allRoutesList);

      // 保存済みの順序
      const savedRoutes = orderRes.data.routes || [];
      
      // 保存済み + 新しいルートを追加
      const orderedRoutes = [];
      savedRoutes.forEach(r => {
        if (routeSet.has(r)) {
          orderedRoutes.push(r);
          routeSet.delete(r);
        }
      });
      // 未登録のルートを末尾に追加
      [...routeSet].sort().forEach(r => orderedRoutes.push(r));

      setRoutes(orderedRoutes);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const newRoutes = [...routes];
    [newRoutes[idx - 1], newRoutes[idx]] = [newRoutes[idx], newRoutes[idx - 1]];
    setRoutes(newRoutes);
  };

  const moveDown = (idx) => {
    if (idx === routes.length - 1) return;
    const newRoutes = [...routes];
    [newRoutes[idx], newRoutes[idx + 1]] = [newRoutes[idx + 1], newRoutes[idx]];
    setRoutes(newRoutes);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveRouteOrder(routes);
      toast.success('表示順を保存しました');
    } catch (error) {
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">ピッキングリスト表示順</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-1" />更新
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="h-4 w-4 mr-1" />{saving ? '保存中...' : '保存'}
            </Button>
          </div>
        </div>
        <p className="text-sm text-slate-500">上下ボタンでルートの表示優先順位を変更できます。上にあるルートが先に表示されます。</p>
      </CardHeader>
      <CardContent>
        {routes.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">ルートが登録されていません</p>
        ) : (
          <div className="space-y-1" data-testid="route-order-list">
            {routes.map((route, idx) => (
              <div
                key={route}
                className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
              >
                <span className="text-sm font-mono text-slate-400 w-8 text-center">{idx + 1}</span>
                <span className="font-bold text-slate-800 flex-1">{route}</span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => moveDown(idx)}
                    disabled={idx === routes.length - 1}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
